import { getInstances } from "@studiometa/js-toolkit";
import { isFunction } from "@studiometa/js-toolkit/utils";
const TARGET_REGEX = /([a-zA-Z]+)(\((.*)\))?/;
const effectCache = /* @__PURE__ */ new Map();
class ActionEvent {
  static modifierSeparator = ".";
  static targetSeparator = " ";
  static effectSeparator = "->";
  /**
   * Timer for debouncing event handling.
   */
  debounceTimer;
  /**
   * The Action instance.
   */
  action;
  /**
   * The event to listen to.
   */
  event;
  /**
   * The modifiers to apply to the event.
   */
  modifiers;
  /**
   * The debounce delay in milliseconds.
   */
  debounceDelay = 100;
  /**
   * Target definition.
   * Ex: `Target Target(.selector)`.
   */
  targetDefinition;
  /**
   * The content of the effect callback function.
   */
  effectDefinition;
  /**
   * Class constructor.
   * @param {T}      action The parent Action instance.
   * @param {string} eventDefinition  The event with its modifiers: `click.prevent.stop`
   * @param {string} effectDefinition The target and effect definition: `Target(.selector)->target.$destroy()`
   */
  constructor(action, eventDefinition, effectDefinition) {
    this.action = action;
    const [event, ...modifiers] = eventDefinition.split(ActionEvent.modifierSeparator);
    this.event = event;
    const processedModifiers = [];
    for (const modifier of modifiers) {
      if (modifier.startsWith("debounce")) {
        processedModifiers.push("debounce");
        this.debounceDelay = parseInt(modifier.replace("debounce", "") || "100");
      } else {
        processedModifiers.push(modifier);
      }
    }
    this.modifiers = processedModifiers;
    let effect = effectDefinition;
    let targetDefinition = "";
    if (effect.includes(ActionEvent.effectSeparator)) {
      [targetDefinition, effect] = effect.split(ActionEvent.effectSeparator);
    }
    this.targetDefinition = targetDefinition.trim();
    this.effectDefinition = effect.trim();
  }
  /**
   * Get the generated function for the defined effect.
   */
  get effect() {
    const { effectDefinition } = this;
    if (!effectCache.has(effectDefinition)) {
      effectCache.set(
        effectDefinition,
        new Function("ctx", "event", "target", "action", "self", `return ${effectDefinition}`)
      );
    }
    return effectCache.get(effectDefinition);
  }
  /**
   * Get the targets object for the defined targets string.
   */
  get targets() {
    const { targetDefinition } = this;
    if (!targetDefinition) {
      return [{ Action: this.action }];
    }
    const parts = targetDefinition.split(ActionEvent.targetSeparator).map((part) => {
      const [, name, , selector] = part.match(TARGET_REGEX) ?? [];
      return [name, selector];
    });
    const targets = [];
    for (const instance of getInstances()) {
      const { name } = instance.__config;
      for (const part of parts) {
        const shouldPush = part[0] === name && (!part[1] || part[1] && instance.$el.matches(part[1]));
        if (shouldPush) {
          targets.push({ [instance.__config.name]: instance });
        }
      }
    }
    return targets;
  }
  /**
   * Handle the defined event and trigger the effect for each defined target.
   */
  handleEvent(event) {
    const { targets, effect, modifiers } = this;
    if (modifiers.includes("prevent")) {
      event.preventDefault();
    }
    if (modifiers.includes("stop")) {
      event.stopPropagation();
    }
    if (modifiers.includes("debounce")) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = window.setTimeout(() => {
        this.executeEffect(targets, effect, event);
      }, this.debounceDelay);
    } else {
      this.executeEffect(targets, effect, event);
    }
  }
  /**
   * Execute the effect for all targets.
   */
  executeEffect(targets, effect, event) {
    for (const target of targets) {
      try {
        const [currentTarget] = Object.values(target).flat();
        const value = effect(target, event, currentTarget, this.action, this.action);
        if (isFunction(value)) {
          value(target, event, currentTarget, this.action, this.action);
        }
      } catch (err) {
        this.action.$warn(err);
      }
    }
  }
  /**
   * Bind the defined event to the given Action instance root element.
   */
  attachEvent() {
    const { event, modifiers } = this;
    this.action.$el.addEventListener(event, this, {
      capture: modifiers.includes("capture"),
      once: modifiers.includes("once"),
      passive: modifiers.includes("passive")
    });
  }
  /**
   * Unbind the event from the given Action instance root element.
   */
  detachEvent() {
    clearTimeout(this.debounceTimer);
    this.action.$el.removeEventListener(this.event, this);
  }
}
export {
  ActionEvent
};
//# sourceMappingURL=ActionEvent.js.map
